import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FileArchive, Play } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useFeed } from "@/hooks/useFeed";
import { LibraryView } from "@/components/library/LibraryView";
import { ShareDialog } from "@/components/library/ShareDialog";
import { ThemeBackdrop } from "@/components/theme/ThemeBackdrop";
import { ThemeDialog } from "@/components/theme/ThemeDialog";
import { Slideshow } from "@/components/theme/Slideshow";
import { pageClass, themeVars } from "@/lib/themes";
import { YearBook, yearBookSlides } from "@/components/yearbook/YearBook";
import { AlbumHead, useAlbumView, ViewToggle } from "@/components/book/ViewToggle";
import { AlbumVisibility } from "@/components/library/AlbumVisibility";
import { AlbumMenu } from "@/components/library/AlbumMenu";
import { MergeDialog } from "@/components/library/MergeDialog";
import { isGuest } from "@/lib/guest";

export default function AlbumPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [album, setAlbum] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [merging, setMerging] = useState(false);
  const [themeOpen, setThemeOpen] = useState(false);
  const [show, setShow] = useState(false);

  useEffect(() => {
    api.get(`/albums/${id}`).then((r) => setAlbum(r.data)).catch(() => navigate("/"));
  }, [id, navigate]);
  const fetchPage = useCallback((cursor) => api.get(`/albums/${id}/items`, { params: { cursor } }).then((r) => r.data), [id]);
  const feed = useFeed(fetchPage);
  const [view, setView] = useAlbumView("album", "thumbs");
  const [ybView, setYbView] = useAlbumView("yearbook", "book");
  const [yb, setYb] = useState(null);
  const loadYb = useCallback(() => api.get(`/albums/${id}/yearbook`).then((r) => setYb(r.data)), [id]);
  useEffect(() => { if (album?.yearbook) loadYb(); }, [album?.yearbook, loadYb]);

  const rename = async () => {
    const name = window.prompt("Rename album", album.name);
    if (name?.trim()) setAlbum((await api.patch(`/albums/${id}`, { name })).data);
  };
  const remove = async () => {
    const msg = album.smart
      ? `Hide smart album “${album.name}”? It won't be recreated unless you restore hidden smart albums in Settings.`
      : `Delete album “${album.name}”? Photos stay in the library.`;
    if (!window.confirm(msg)) return;
    await api.delete(`/albums/${id}`);
    navigate("/");
  };
  const convert = async () => {
    setAlbum((await api.post(`/albums/${id}/convert`)).data);
    toast.success("Converted to a regular album");
  };
  const albumCtx = album && {
    unsorted: !!album.unsorted,
    canRemove: album.can_edit,
    canEdit: album.can_edit,
    onRemove: async (ids) => {
      await api.post(`/albums/${id}/items/remove`, { item_ids: ids });
      toast(`Removed ${ids.length} from album`);
    },
    onCover: async (itemId) => {
      try {
        setAlbum((await api.patch(`/albums/${id}`, { cover_item_id: itemId })).data);
        toast.success("Cover updated");
      } catch (e) {
        toast.error(errMsg(e));
      }
    },
  };

  if (!album) return null;
  const viewBtn = <ViewToggle value={view} onChange={setView} />;
  if (album.yearbook && ybView === "book") {
    const tools = (
      <>
        {album.item_count > 0 && <button className="tool-btn" onClick={() => setShow(true)} data-testid="album-slideshow-button"><Play size={16} /> <span>Slideshow</span></button>}
        <ViewToggle value={ybView} onChange={setYbView} />
        {album.item_count > 0 && <a className="tool-btn" href={`${api.defaults.baseURL}/albums/${id}/zip`} download aria-label="Download all" data-testid="album-zip-button"><FileArchive size={16} /> <span>Download all</span></a>}
        <AlbumMenu album={album} onShare={() => setShareOpen(true)} onRename={rename} onTheme={() => setThemeOpen(true)}
          onRemove={() => window.confirm(`Hide “${album.name}”? It won't come back unless you restore hidden smart albums in Settings.`) && api.delete(`/albums/${id}`).then(() => navigate("/"))} />
      </>
    );
    return (
      <div className={`themed-album ${pageClass(album.theme)}`} style={themeVars(album.theme)} data-testid="album-page" data-theme={album.theme?.key}>
        <ThemeBackdrop theme={album.theme} />
        {yb && <YearBook title={album.name} data={yb} toolbar={tools} canEdit={album.can_edit}
          head={(t) => <AlbumHead album={album} onBack={() => navigate("/")}>{t}</AlbumHead>}
          onExclude={async (itemId) => { await albumCtx.onRemove([itemId]); loadYb(); }}
          onCover={async (itemId) => { await albumCtx.onCover(itemId); loadYb(); }}
          onItemChange={(d) => d && setYb((y) => ({ ...y, highlights: y.highlights.map((h) => (h.id === d.id ? { ...h, ...d } : h)),
            chapters: y.chapters.map((c) => ({ ...c, items: c.items.map((i) => (i.id === d.id ? { ...i, ...d } : i)) })) }))} />}
        <ShareDialog open={shareOpen} onOpenChange={setShareOpen} album={album} onChange={setAlbum} />
        {show && yb && <Slideshow items={yearBookSlides(yb)} hasMore={false} theme={album.theme} onClose={() => setShow(false)} />}
      </div>
    );
  }
  return (
    <div className={`themed-album ${pageClass(album.theme)}`} style={themeVars(album.theme)} data-testid="album-page" data-theme={album.theme?.key}>
      <ThemeBackdrop theme={album.theme} />
      <LibraryView
        feed={feed}
        space={album.space}
        timeline={false}
        album={albumCtx}
        view={album.yearbook ? "thumbs" : view}
        total={album.item_count}
        empty="This album is empty. Select photos in the library and choose “Add to album”."
        head={(tools) => <AlbumHead album={album} onBack={() => navigate("/")}>{tools}</AlbumHead>}
        toolbar={(
          <>
            {album.item_count > 0 && (
              <button className="tool-btn" onClick={() => setShow(true)} data-testid="album-slideshow-button"><Play size={16} /> <span>Slideshow</span></button>
            )}
            {album.yearbook ? <ViewToggle value={ybView} onChange={setYbView} /> : viewBtn}
            {album.item_count > 0 && !album.unsorted && (
              <a className="tool-btn" href={`${api.defaults.baseURL}/albums/${id}/zip`} download aria-label="Download all" data-testid="album-zip-button"><FileArchive size={16} /> <span>Download all</span></a>
            )}
            {!isGuest() && !album.unsorted && <AlbumVisibility album={album} onChange={setAlbum} />}
            <AlbumMenu album={album} onShare={() => setShareOpen(true)} onRename={rename} onConvert={convert} onRemove={remove}
              onTheme={() => setThemeOpen(true)} onMerge={() => setMerging(true)} />
          </>
        )}
      />
      <ShareDialog open={shareOpen} onOpenChange={setShareOpen} album={album} onChange={setAlbum} />
      {album.can_change_visibility && <MergeDialog open={merging} onClose={() => setMerging(false)} source={album} />}
      {album.can_theme && <ThemeDialog open={themeOpen} onOpenChange={setThemeOpen} album={album} onChange={setAlbum} />}
      {show && feed.items.length > 0 && (
        <Slideshow items={album.yearbook && yb ? yearBookSlides(yb) : feed.items} hasMore={album.yearbook ? false : feed.hasMore} onLoadMore={feed.loadMore} theme={album.theme} onClose={() => setShow(false)} />
      )}
    </div>
  );
}
